import React from "react";
import {
  Box,
  Flex,
  Text,
  Heading,
  Divider,
  SimpleGrid,
  Badge,
  Icon,
  VStack,
} from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";
import { useLocation } from "react-router-dom";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";

export default function BookingConfirmed() {
  const location = useLocation();

  // FULL DATA FROM BookingStatus.jsx
  const {
    leadId,
    bookingId, // The actual booking ID
    amount,
    systemCalculated,
    tokenAmount,
    bookingData,
    customerData,
    transactionID,
  } = location.state || {};

  if (!leadId || !bookingData || !customerData) {
    return (
      <Box bg="#F3F6FD" minH="100vh" p={5}>
        <Text color="red.500">No booking data found.</Text>
      </Box>
    );
  }

  const formatTime12 = (timeStr) => {
    if (!timeStr) return "";
    let [hours, minutes] = timeStr.split(":").map(Number);
    const modifier = hours >= 12 ? "PM" : "AM";
    if (hours > 12) hours -= 12;
    if (hours === 0) hours = 12;
    return `${hours}:${minutes.toString().padStart(2, "0")} ${modifier}`;
  };

  return (
    <Box bg="#F3F6FD" minH="100vh" p={{ base: 4, md: 8 }}>
      {/* Header */}
      <Flex mb={6} align="center" justify="space-between">
        <Box mb={4}>
          <BreadCrumbMaker
            items={[
              { title: "Dashboard", link: "/" },
              { title: "Bookings", link: "/bookings" },
              { title: "Add Leads", link: "/bookings/lead/results" },
              {
                title: "Booking Status",
                link: "/bookings/lead/booking-status",
              },
            ]}
          />
        </Box>
      </Flex>

      {/* Success */}
      <Box textAlign="center" mb={10}>
        <Icon as={CheckCircleIcon} w={20} h={20} color="green.400" />
        <Heading color="green.500" mt={4}>
          Booking Confirmed!
        </Heading>
        <Text mt={2} color="gray.600">
          Payment received successfully & your booking has been confirmed.
        </Text>
      </Box>

      <Box w={{ base: "100%", lg: "85%" }} mx="auto">
        {/* Booking Summary */}
        <Box
          bg="white"
          p={6}
          rounded="md"
          border="1px solid #E8EDF7"
          boxShadow="sm"
          mb={6}
        >
          <Heading size="md" mb={5} color="#2C3653">
            Booking Summary
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Booking ID
              </Text>
              <Text fontWeight="600">{bookingId || leadId}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Booking Date
              </Text>
              <Text fontWeight="600">{bookingData.pickupdate}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Booking Time
              </Text>
              <Text fontWeight="600">
                {formatTime12(bookingData.pickuptime)}
              </Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Car Model
              </Text>
              <Text fontWeight="600">{bookingData.carDetails?.name}</Text>
            </Box>

            {bookingData.tripType === "RT" && bookingData.returndate && (
              <Box>
                <Text fontSize="sm" color="#6B7280">
                  Return Date
                </Text>
                <Text fontWeight="600">{bookingData.returndate}</Text>
              </Box>
            )}
          </SimpleGrid>

          <Divider my={4} />

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Pickup Location
              </Text>
              <Text fontWeight="600">{bookingData.from}</Text>
            </Box>

            {bookingData.stops && bookingData.stops.length > 0 && (
              <Box>
                <Text fontSize="sm" color="#6B7280">
                  Stops
                </Text>
                <VStack align="start" spacing={1}>
                  {bookingData.stops.map((stop, idx) => (
                    <Text key={idx} fontSize="sm" fontWeight="500">
                      Stop {idx + 1}:{" "}
                      {typeof stop === "object" && stop !== null
                        ? stop.name || stop
                        : stop}
                    </Text>
                  ))}
                </VStack>
              </Box>
            )}

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Drop Location
              </Text>
              <Text fontWeight="600">{bookingData.to}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Discount
              </Text>
              <Text fontWeight="600">₹ {systemCalculated - amount}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Token Amount
              </Text>
              <Text fontWeight="600">₹ {tokenAmount}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Final Price
              </Text>
              <Text fontWeight="600">₹ {amount}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Payment Status
              </Text>
              <Badge colorScheme="green" px={3} py={1} rounded="md">
                Paid
              </Badge>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Transaction ID
              </Text>
              <Text fontWeight="600">{transactionID}</Text>
            </Box>
          </SimpleGrid>
        </Box>

        {/* Customer Info */}
        <Box
          bg="white"
          p={6}
          rounded="md"
          border="1px solid #E8EDF7"
          boxShadow="sm"
        >
          <Heading size="md" mb={5} color="#2C3653">
            Customer Details
          </Heading>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box>
              <Text fontSize="sm" color="#6B7280">
                Customer Name
              </Text>
              <Text fontWeight="600">{customerData.name}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Mobile Number
              </Text>
              <Text fontWeight="600">{customerData.mobile}</Text>
            </Box>

            <Box>
              <Text fontSize="sm" color="#6B7280">
                Email ID
              </Text>
              <Text fontWeight="600">{customerData.email || "N/A"}</Text>
            </Box>
          </SimpleGrid>

          {bookingData.comments && (
            <Box mt={5}>
              <Text fontSize="sm" color="#6B7280">
                Notes
              </Text>
              <Text fontWeight="600">{bookingData.comments}</Text>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
